/**
 * 테마 유틸리티 함수
 * Theme utility functions for tweakcn custom theme support
 */

/**
 * CSS 스타일시트에서 theme-* 클래스를 자동으로 감지합니다.
 * Detects theme-* classes from CSS stylesheets
 * 
 * @returns 감지된 테마 이름 배열 (예: ['default', 'slate', 'rose', ...])
 */
export function detectThemeClasses(): string[] {
  const themeClasses: Set<string> = new Set()
  
  try {
    // 모든 스타일시트 순회
    const styleSheets = Array.from(document.styleSheets)
    
    for (const styleSheet of styleSheets) {
      try {
        // CORS 제한으로 인해 접근 불가능한 스타일시트는 건너뛰기
        const rules = styleSheet.cssRules || styleSheet.rules
        
        if (!rules) continue
        
        for (const rule of Array.from(rules)) {
          if (rule instanceof CSSStyleRule) {
            const selector = rule.selectorText
            
            // .theme-{name} 형식의 클래스 선택자 찾기
            const themeMatch = selector.match(/\.theme-([a-zA-Z0-9_-]+)/)
            if (themeMatch && themeMatch[1]) {
              themeClasses.add(themeMatch[1])
            }
          }
        }
      } catch {
        // CORS 오류 등으로 인한 접근 불가 스타일시트는 무시
        continue
      }
    }
    
    // document에서 직접 클래스 찾기 (동적으로 추가된 스타일)
    const allClasses = Array.from(document.querySelectorAll('[class*="theme-"]'))
    for (const element of allClasses) {
      const classList = Array.from(element.classList)
      for (const className of classList) {
        const themeMatch = className.match(/^theme-([a-zA-Z0-9_-]+)$/)
        if (themeMatch && themeMatch[1]) {
          themeClasses.add(themeMatch[1])
        }
      }
    }
  } catch (error) {
    console.warn('[theme-utils] Failed to detect theme classes:', error)
  }
  
  return Array.from(themeClasses)
}

/**
 * 현재 문서의 모든 테마 클래스를 반환합니다.
 * Returns all theme classes currently in the document
 * 
 * @returns 테마 이름 배열
 */
export function getAllThemeClasses(): string[] {
  return detectThemeClasses()
}

/**
 * 테마 이름이 유효한지 검사합니다.
 * Validates if a theme name is valid
 * 
 * @param theme - 검사할 테마 이름
 * @returns 유효하면 true
 */
export function isValidTheme(theme: string): boolean {
  if (!theme || typeof theme !== 'string') {
    return false
  }
  
  // theme-{name} 형식인지 확인
  const themeClasses = detectThemeClasses()
  return themeClasses.includes(theme)
}

/**
 * 디폴트 테마가 항상 포함되도록 보장합니다.
 * Ensures that the default theme is always included
 * 
 * @param themes - 테마 이름 배열
 * @returns 디폴트 테마가 포함된 테마 배열 (default가 맨 앞에 위치)
 */
export function ensureDefaultTheme(themes: string[]): string[] {
  const uniqueThemes = Array.from(new Set(themes))
  
  // default가 없으면 경고하고 추가
  if (!uniqueThemes.includes('default')) {
    console.warn('[theme-utils] Default theme not found. Adding default theme to list.')
    return ['default', ...uniqueThemes]
  }
  
  // default를 맨 앞에 배치
  const otherThemes = uniqueThemes.filter(t => t !== 'default')
  return ['default', ...otherThemes]
}

/**
 * 디폴트 테마 이름을 반환합니다.
 * Returns the default theme name
 * 
 * @returns 항상 'default'
 */
export function getDefaultTheme(): string {
  return 'default'
}

/**
 * 테마가 존재하는지 검증하고, 없으면 디폴트 테마를 반환합니다.
 * Validates if a theme exists, returns default if not found
 * 
 * @param theme - 검증할 테마 이름
 * @returns 유효한 테마 이름 (없으면 'default')
 */
export function validateThemeExists(theme: string): string {
  if (!theme || typeof theme !== 'string') {
    return getDefaultTheme()
  }
  
  const themeClasses = detectThemeClasses()
  
  if (themeClasses.includes(theme)) {
    return theme
  }
  
  console.warn(`[theme-utils] Theme '${theme}' not found. Using default theme.`)
  return getDefaultTheme()
}

/**
 * 로컬 스토리지에서 저장된 테마를 가져옵니다.
 * Gets stored theme from localStorage
 * 
 * @param storageKey - 스토리지 키 (기본값: 'tweakcn-theme')
 * @returns 저장된 테마 이름 또는 null
 */
export function getStoredTheme(storageKey: string = 'tweakcn-theme'): string | null {
  try {
    return localStorage.getItem(storageKey)
  } catch (error) {
    console.warn('[theme-utils] Failed to get stored theme:', error)
    return null
  }
}

/**
 * 테마를 로컬 스토리지에 저장합니다.
 * Saves theme to localStorage
 * 
 * @param theme - 저장할 테마 이름
 * @param storageKey - 스토리지 키 (기본값: 'tweakcn-theme')
 */
export function setStoredTheme(theme: string, storageKey: string = 'tweakcn-theme'): void {
  try {
    localStorage.setItem(storageKey, theme)
  } catch (error) {
    console.warn('[theme-utils] Failed to save theme:', error)
  }
}

/**
 * 문서의 루트 요소에 테마 클래스를 적용합니다.
 * Applies theme class to document root element
 * 
 * @param theme - 적용할 테마 이름
 * @returns 성공 여부
 */
export function applyTheme(theme: string): boolean {
  if (!theme || typeof theme !== 'string') {
    return false
  }
  
  try {
    const root = document.documentElement
    
    // 모든 theme-* 클래스 제거
    const themeClasses = detectThemeClasses()
    for (const themeClass of themeClasses) {
      root.classList.remove(`theme-${themeClass}`)
    }
    
    // 새 테마 클래스 추가
    root.classList.add(`theme-${theme}`)
    
    return true
  } catch (error) {
    console.error('[theme-utils] Failed to apply theme:', error)
    return false
  }
}
